import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import axiosInstance from '../../api/axiosConfig';
import './FormStyles.css';
import receptionBg from '../../assets/images/backgrounds/reception-bg.png';

const MeetingForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const isPublic = location.pathname.startsWith('/visitor/form');

    const [formData, setFormData] = useState({
        visitorName: '',
        email: '',
        phone: '',
        company: '',
        meetingWith: '',
        meetingTime: '',
        meetingDuration: '',
        purpose: '',
        notes: ''
    });

    const API_URL = process.env.REACT_APP_API_URL || '';

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (isPublic) {
                await axios.post(
                    `${API_URL}/api/public/visitor-forms`,
                    {
                        formType: 'meeting',
                        formData,
                        notes: formData.notes
                    }
                );
            } else {
                await axiosInstance.post(
                    '/api/forms',
                    {
                        formType: 'meeting',
                        formData,
                        notes: formData.notes
                    }
                );
            }

            setSuccess(true);
            setTimeout(() => {
                navigate(isPublic ? '/visitor' : '/purpose-selection');
            }, 2000);
        } catch (error) {
            alert('Error submitting form: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate(isPublic ? '/visitor' : '/purpose-selection');
    };

    return (
        <div className="form-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="form-wrapper card fade-in">
                <div className="form-header">
                    <img src="/logo.png" alt="Logo" className="app-logo" />
                    <div className="form-header-text">
                        <h2>Meeting/Appointment Registration</h2>
                        <p>Scheduled meeting check-in</p>
                    </div>
                    <div className="form-header-actions">
                        {!isPublic && user && (
                            <div className="form-user-info">
                                <strong>{user?.fullName || user?.username}</strong>
                            </div>
                        )}
                        <button
                            type="button"
                            className="btn-back-form"
                            onClick={handleBack}
                        >
                            ← Back
                        </button>
                    </div>
                </div>

                {success && (
                    <div className="alert alert-success">
                        ✅ Meeting registered successfully! Redirecting...
                    </div>
                )}

                <form onSubmit={handleSubmit} className="visitor-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="visitorName" className="form-label">Visitor Name *</label>
                            <input
                                type="text"
                                id="visitorName"
                                name="visitorName"
                                className="form-input"
                                value={formData.visitorName}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email" className="form-label">Email Address *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className="form-input"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="phone" className="form-label">Phone Number *</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                className="form-input"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="company" className="form-label">Company/Organization</label>
                            <input
                                type="text"
                                id="company"
                                name="company"
                                className="form-input"
                                value={formData.company}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="meetingWith" className="form-label">Meeting With *</label>
                            <input
                                type="text"
                                id="meetingWith"
                                name="meetingWith"
                                className="form-input"
                                value={formData.meetingWith}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="meetingTime" className="form-label">Meeting Time</label>
                            <input
                                type="time"
                                id="meetingTime"
                                name="meetingTime"
                                className="form-input"
                                value={formData.meetingTime}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="meetingDuration" className="form-label">Expected Duration</label>
                            <select
                                id="meetingDuration"
                                name="meetingDuration"
                                className="form-select"
                                value={formData.meetingDuration}
                                onChange={handleChange}
                            >
                                <option value="">Select duration...</option>
                                <option value="15min">15 minutes</option>
                                <option value="30min">30 minutes</option>
                                <option value="1hour">1 hour</option>
                                <option value="2hours">2 hours</option>
                                <option value="halfday">Half day</option>
                                <option value="fullday">Full day</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="purpose" className="form-label">Meeting Purpose *</label>
                            <input
                                type="text"
                                id="purpose"
                                name="purpose"
                                className="form-input"
                                value={formData.purpose}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="notes" className="form-label">Additional Notes</label>
                        <textarea
                            id="notes"
                            name="notes"
                            className="form-textarea"
                            value={formData.notes}
                            onChange={handleChange}
                            rows="3"
                        ></textarea>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleBack}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? 'Submitting...' : 'Register Meeting'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MeetingForm;
